﻿import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type DocumentTemplateKey, type DocumentTemplateRow } from "../app/api";
import { TemplateWysiwygEditor } from "../components/common/TemplateWysiwygEditor";

type EditorTarget = "subject" | "body";
type TemplateLane = "digital" | "analog" | "trigger";
type EditorCommand =
  | "bold"
  | "italic"
  | "underline"
  | "strikeThrough"
  | "insertUnorderedList"
  | "insertOrderedList"
  | "justifyLeft"
  | "justifyCenter"
  | "justifyRight"
  | "removeFormat"
  | "undo"
  | "redo"
  | "fontName"
  | "fontSize"
  | "createLink"
  | "unlink"
  | "clear";

export const TemplatesPage = () => {
  const qc = useQueryClient();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<DocumentTemplateKey>("invitation_digital");
  const [templateLane, setTemplateLane] = useState<TemplateLane>("digital");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [activeEditor, setActiveEditor] = useState<EditorTarget>("body");
  const [editorViewMode, setEditorViewMode] = useState<{ subject: "rich" | "html"; body: "rich" | "html" }>({
    subject: "rich",
    body: "rich"
  });
  const [insertSignal, setInsertSignal] = useState<{ nonce: number; token: string } | null>(null);
  const [commandSignal, setCommandSignal] = useState<{ nonce: number; command: EditorCommand; value?: string } | null>(null);
  const [placeholderQuery, setPlaceholderQuery] = useState("");
  const [openPlaceholderGroupKey, setOpenPlaceholderGroupKey] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [testRecipientEmail, setTestRecipientEmail] = useState("");
  const [testMailStatus, setTestMailStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  const getSubjectLineCount = (value: string) => {
    const normalized = String(value ?? "")
      .replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\r\n/g, "\n")
      .trim();
    if (!normalized) return 1;
    return normalized.split("\n").length;
  };

  const [subjectLineError, setSubjectLineError] = useState<string | null>(null);
  const [subjectLengthError, setSubjectLengthError] = useState<string | null>(null);
  const [bodyLengthError, setBodyLengthError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ["document-templates"],
    queryFn: () => api.documentTemplates()
  });

  const visibleTemplates = useMemo(
    () => (templatesQuery.data?.templates ?? []).filter((row) => row.key !== "ballot_analog"),
    [templatesQuery.data?.templates]
  );

  const resolveTemplateLane = (row: DocumentTemplateRow): TemplateLane => {
    if (row.key.startsWith("trigger_")) return "trigger";
    return row.kanal;
  };

  const templatesInLane = useMemo(
    () => visibleTemplates.filter((row) => resolveTemplateLane(row) === templateLane),
    [visibleTemplates, templateLane]
  );

  const selectedTemplate = templatesInLane.find((row) => row.key === selectedTemplateKey) ?? templatesInLane[0] ?? visibleTemplates[0];

  useEffect(() => {
    if (!selectedTemplate) return;
    const lane = resolveTemplateLane(selectedTemplate);
    if (lane !== templateLane) setTemplateLane(lane);
  }, [selectedTemplate, templateLane]);

  useEffect(() => {
    const rows = templatesInLane.length > 0 ? templatesInLane : visibleTemplates;
    if (!rows.length) return;
    const selected = rows.find((row) => row.key === selectedTemplateKey) ?? rows[0];
    if (selected.key !== selectedTemplateKey) {
      setSelectedTemplateKey(selected.key);
      return;
    }
    setSubject(selected.subject);
    setBody(selected.body);
    setSaveStatus(null);
    setSubjectLineError(null);
    setSubjectLengthError(null);
    setBodyLengthError(null);
    setTestMailStatus(null);
  }, [templatesInLane, visibleTemplates, selectedTemplateKey]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.updateDocumentTemplate(selectedTemplateKey, {
        subject,
        body,
        expectedUpdatedAt: selectedTemplate?.updatedAt
      });
      return api.documentTemplates();
    },
    onSuccess: (nextTemplates) => {
      qc.setQueryData(["document-templates"], nextTemplates);
      setSaveStatus({ type: "ok", text: "Vorlage wurde gespeichert." });
    },
    onError: (error) => {
      setSaveStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Vorlage konnte nicht gespeichert werden."
      });
    }
  });

  const mailPreviewMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error("Keine Vorlage ausgewählt.");
      return api.previewDocumentTemplateMail({
        templateKey: selectedTemplate.key,
        eventType: selectedTemplate.eventType
      });
    }
  });
  const pdfPreviewMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error("Keine Vorlage ausgewählt.");
      return api.previewDocumentTemplatePdf({
        templateKey: selectedTemplate.key,
        eventType: selectedTemplate.eventType
      });
    },
    onSuccess: (blob) => {
      const nextUrl = URL.createObjectURL(blob);
      setPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return nextUrl;
      });
    },
    onError: () => {
      setPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  });

  const sendTestMailMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error("Keine Vorlage ausgewählt.");
      const recipientEmail = testRecipientEmail.trim();
      if (!recipientEmail) throw new Error("Bitte Empfänger-E-Mail für die Testmail eingeben.");
      return api.sendDocumentTemplatePreviewMailTest({
        templateKey: selectedTemplate.key,
        eventType: selectedTemplate.eventType,
        recipientEmail
      });
    },
    onSuccess: (result) => {
      setTestMailStatus({
        type: "ok",
        text: `Testmail an ${result.recipientEmail} versendet${result.messageId ? ` (Message-ID: ${result.messageId})` : ""}.`
      });
    },
    onError: (error) => {
      setTestMailStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Testmail konnte nicht versendet werden."
      });
    }
  });

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  const onSubjectChange = (next: string) => {
    setSaveStatus(null);
    if (next.length > 400) {
      setSubjectLengthError("Betreff darf maximal 400 Zeichen haben.");
      return;
    }
    setSubjectLengthError(null);
    const lineCount = getSubjectLineCount(next);
    if (lineCount > 3) {
      setSubjectLineError("Betreff darf maximal 3 Zeilen haben.");
      return;
    }
    setSubjectLineError(null);
    setSubject(next);
  };

  const onBodyChange = (next: string) => {
    setSaveStatus(null);
    if (next.length > 20_000) {
      setBodyLengthError("Textvorlage darf maximal 20000 Zeichen haben.");
      return;
    }
    setBodyLengthError(null);
    setBody(next);
  };

  const placeholders = templatesQuery.data?.placeholders ?? [];
  const apiGroups = templatesQuery.data?.placeholderGroups ?? [];
  const isAnalogTemplate = selectedTemplate?.kanal === "analog";

  const groups = useMemo(() => {
    if (apiGroups.length > 0) return apiGroups;
    return [{ key: "sonstige", label: "Sonstige", tokens: placeholders }];
  }, [apiGroups, placeholders]);

  const filteredGroups = useMemo(() => {
    const query = placeholderQuery.trim().toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        tokens: group.tokens.filter((token) => (query ? token.toLowerCase().includes(query) : true))
      }))
      .filter((group) => group.tokens.length > 0);
  }, [groups, placeholderQuery]);

  useEffect(() => {
    if (!openPlaceholderGroupKey) return;
    const stillExists = filteredGroups.some((group) => group.key === openPlaceholderGroupKey);
    if (!stillExists) setOpenPlaceholderGroupKey(null);
  }, [filteredGroups, openPlaceholderGroupKey]);

  const fireCommand = (command: EditorCommand, value?: string) => {
    setCommandSignal({ nonce: Date.now(), command, value });
  };

  const plainBodyText = useMemo(() => {
    const text = String(body ?? "")
      .replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\r\n/g, "\n");
    return text.trim();
  }, [body]);

  const plainSubjectText = useMemo(() => {
    const text = String(subject ?? "")
      .replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\r\n/g, "\n");
    return text.trim();
  }, [subject]);

  const wordCount = useMemo(() => {
    if (!plainBodyText) return 0;
    return plainBodyText.split(/\s+/).filter(Boolean).length;
  }, [plainBodyText]);

  const characterCount = useMemo(() => plainBodyText.length, [plainBodyText]);

  const formatDraftStatus = (value?: string | null) => {
    if (!value) return "Letzte Speicherung: vor kurzem";
    const stamp = new Date(value);
    if (Number.isNaN(stamp.getTime())) return "Letzte Speicherung: vor kurzem";
    const diffMs = Date.now() - stamp.getTime();
    const diffMin = Math.max(1, Math.floor(diffMs / 60_000));
    if (diffMin < 60) return `Letzte Speicherung: vor ${diffMin} Minute${diffMin === 1 ? "" : "n"}`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `Letzte Speicherung: vor ${diffHours} Stunde${diffHours === 1 ? "" : "n"}`;
    return `Letzte Speicherung: ${stamp.toLocaleDateString("de-DE")} ${stamp.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
  };

  const normalizeTemplateLabel = (label: string) => label.replace(/^Trigger:\s*/i, "").trim();
  const normalizePreviewHtml = (html: string) => {
    const baseStyle = "<style>html,body{background:#fff!important;}</style>";
    if (/<head[^>]*>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${baseStyle}`);
    return `${baseStyle}${html}`;
  };

  const openPreviewPanel = () => {
    setPreviewVisible(true);
    setTestMailStatus(null);
    refreshPreview();
  };

  const renderPlaceholderGroupIcon = (groupKey: string) => {
    const key = groupKey.toLowerCase();
    if (key.includes("verwaltung")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4.5 10.5 12 4.8l7.5 5.7v8.2H4.5z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M9.2 18.7v-4.3h5.6v4.3" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    }
    if (key.includes("objekt")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4.9 8.2h14.2v10.9H4.9z" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8.1 4.9h7.8v3.3H8.1zM8.2 12h2.6M13.2 12h2.6M8.2 15.4h2.6M13.2 15.4h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    }
    if (key.includes("eigent")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
          <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    }
    if (key.includes("einheit")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="5.1" y="4.8" width="13.8" height="14.8" rx="2.1" stroke="currentColor" strokeWidth="1.85" />
          <path d="M8.3 9h2.6M13.1 9h2.6M8.3 13.2h2.6M13.1 13.2h2.6" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" />
          <path d="M9.2 19.6v-2.9h5.6v2.9" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (key.includes("beschluss")) {
      return (
        <svg viewBox="0 0 64 64" fill="none">
          <path d="M14 49h36" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <rect x="18" y="38" width="5" height="8" rx="1.2" stroke="currentColor" strokeWidth="2.4" />
          <rect x="29" y="32" width="5" height="14" rx="1.2" stroke="currentColor" strokeWidth="2.4" />
          <rect x="40" y="24" width="5" height="22" rx="1.2" stroke="currentColor" strokeWidth="2.4" />
          <path d="M18 30 28.5 20.5 36 26l12-12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M44.5 14H48v3.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (key.includes("ergebnis")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.6" />
          <path d="m9.2 12.3 1.9 2 3.7-4.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (key.includes("zugang")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="5.3" y="11.1" width="13.4" height="8" rx="2.1" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8.7 11V8.7a3.3 3.3 0 1 1 6.6 0V11" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    }
    if (key.includes("email")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="4.8" y="7.1" width="14.4" height="9.8" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
          <path d="m5.8 8.2 6.2 4.6 6.2-4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (key.includes("datum")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="4.8" y="6.1" width="14.4" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8.2 4.8v2.6M15.8 4.8v2.6M4.8 9.4h14.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    }
    if (key.includes("layout")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4.7 19.3h3.6l9.7-9.7a1.8 1.8 0 0 0 0-2.6l-1-1a1.8 1.8 0 0 0-2.6 0L4.7 15.7v3.6Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="m12.9 7.5 3.6 3.6" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    }
    if (key.includes("stimme")) {
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="13.8" y="4.8" width="5.2" height="3.8" rx="0.8" stroke="currentColor" strokeWidth="1.6" />
          <path d="M10.4 19.2v-6.9a1.3 1.3 0 1 0-2.6 0V16l-1.5-1.2a1.2 1.2 0 0 0-1.6 1.8l3.3 3.1c.5.4 1 .7 1.6.7h2.4c1.5 0 2.8-1.2 2.8-2.8v-3.5a1.2 1.2 0 1 0-2.4 0v1.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  };

  const refreshPreview = () => {
    if (!selectedTemplate) return;
    if (selectedTemplate.kanal === "analog") {
      pdfPreviewMutation.mutate();
      return;
    }
    mailPreviewMutation.mutate();
  };

  useEffect(() => {
    if (!selectedTemplate) return;
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.key]);

  return (
    <div className="templates-v2-page">
      <section className="page-header panel resolutions-v2-page-header imports-v2-page-header templates-v2-page-header">
        <div className="resolutions-v2-title">
            <span className="resolutions-v2-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4.8 8a1.8 1.8 0 0 1 1.8-1.8h4.2l1.5 1.7h5.1A1.8 1.8 0 0 1 19.2 9v8.2a1.8 1.8 0 0 1-1.8 1.8H6.6a1.8 1.8 0 0 1-1.8-1.8V8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
            </span>
          <div className="resolutions-v2-title-copy">
            <span className="resolutions-v2-title-main">Vorlagen</span>
            <span className="resolutions-v2-title-sub">Gestalten Sie Vorlagen mit Platzhaltern.</span>
          </div>
        </div>
        </section>

      <section className="templates-v2-lane-grid" aria-label="Vorlagentyp wählen">
        <button type="button" className={`templates-v2-lane-card${templateLane === "digital" ? " is-active" : ""}`} onClick={() => setTemplateLane("digital")}>
          <span className="templates-v2-lane-dot is-digital" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M7 3.8h7.2l2.8 2.8v13.6H7z" stroke="currentColor" strokeWidth="1.7" />
              <path d="M14.2 3.8v2.9h2.8M9.6 10.2h4.9M9.6 13h5.9M9.6 15.8h4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </span>
            <span className="templates-v2-lane-copy">
              <strong>Digitale Dokumente</strong>
              <small>Einladung, Erinnerung, Ergebnis</small>
            </span>
        </button>
        <button type="button" className={`templates-v2-lane-card${templateLane === "analog" ? " is-active" : ""}`} onClick={() => setTemplateLane("analog")}>
            <span className="templates-v2-lane-dot is-analog" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="7.2" y="4.8" width="9.6" height="4.2" rx="0.8" stroke="currentColor" strokeWidth="1.8" />
                <rect x="5.2" y="9.4" width="13.6" height="7.2" rx="1.8" stroke="currentColor" strokeWidth="1.8" />
                <rect x="7.4" y="14.6" width="9.2" height="4.6" rx="0.9" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="15.9" cy="12.9" r="0.9" fill="currentColor" />
              </svg>
            </span>
            <span className="templates-v2-lane-copy">
              <strong>Analoge Dokumente</strong>
              <small>Einladung, Erinnerung, Ergebnis</small>
            </span>
          </button>
        <button type="button" className={`templates-v2-lane-card${templateLane === "trigger" ? " is-active" : ""}`} onClick={() => setTemplateLane("trigger")}>
          <span className="templates-v2-lane-dot is-trigger" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4.8 7.1h14.4v9.8H4.8z" stroke="currentColor" strokeWidth="1.7" />
              <path d="m5.8 8.2 6.2 4.6 6.2-4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
            <span className="templates-v2-lane-copy">
              <strong>Trigger Nachrichten</strong>
              <small>Benarichtungstexte an Verwaltung</small>
            </span>
        </button>
      </section>

      <section className="templates-main-section templates-v2-main-grid">
        <div className="templates-v2-column templates-v2-column--left">
          <div className="templates-v2-pane templates-v2-unified-editor-pane">
            <div className="templates-v2-step-head templates-v2-step-head--with-field">
              <span className="templates-v2-step-icon is-step-1" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M7 3.8h7.2l2.8 2.8v13.6H7z" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M14.2 3.8v2.9h2.8M9.6 10.2h4.9M9.6 13h5.9M9.6 15.8h4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </span>
              <h3 className="templates-v2-step-title">Vorlage wählen</h3>
              <select className="templates-v2-template-select" value={selectedTemplate?.key ?? selectedTemplateKey} onChange={(e) => setSelectedTemplateKey(e.target.value as DocumentTemplateKey)}>
                {(templatesInLane.length > 0 ? templatesInLane : visibleTemplates).map((row: DocumentTemplateRow) => (
                  <option key={row.key} value={row.key}>
                    {normalizeTemplateLabel(row.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="templates-v2-pane templates-v2-placeholders-pane">
            <div className="template-collapsible-header">
              <div className="templates-v2-step-head">
                <span className="templates-v2-step-icon is-step-2" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M9.1 7.2 5.8 12l3.3 4.8M14.9 7.2l3.3 4.8-3.3 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12.9 6.8 11.1 17.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
                <h3 className="templates-v2-step-title">Platzhalter</h3>
              </div>
              <div className="templates-v2-search-field templates-v2-placeholder-head-search">
                <span className="templates-v2-search-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="11.2" cy="11.2" r="5.7" stroke="currentColor" strokeWidth="1.8" />
                    <path d="m15.4 15.4 3.2 3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <input value={placeholderQuery} onChange={(e) => setPlaceholderQuery(e.target.value)} placeholder="Platzhalter suchen..." />
              </div>
            </div>
            <div className="template-placeholder-shared-box">
                <div className={`template-placeholder-groups${openPlaceholderGroupKey ? "" : " is-collapsed-view"}`}>
                {(openPlaceholderGroupKey
                  ? filteredGroups.filter((group) => group.key === openPlaceholderGroupKey)
                  : filteredGroups
                ).map((group) => (
                    <div key={group.key} className="template-placeholder-group">
                      <button
                        type="button"
                        className="template-placeholder-group-title template-placeholder-group-trigger"
                        onClick={() => setOpenPlaceholderGroupKey((prev) => (prev === group.key ? null : group.key))}
                        >
                        <span className="template-placeholder-group-label">
                          <span className="template-placeholder-group-icon" aria-hidden="true">{renderPlaceholderGroupIcon(group.key)}</span>
                          <span>{group.label}</span>
                          <span className="template-placeholder-group-count">{group.tokens.length}</span>
                        </span>
                        <span className="template-placeholder-group-arrow">{openPlaceholderGroupKey === group.key ? "⌄" : "›"}</span>
                      </button>
                    {openPlaceholderGroupKey === group.key ? (
                      <div className="template-wysiwyg-placeholder-palette" aria-label={`Platzhalter ${group.label}`}>
                        {group.tokens.map((token) => (
                          <button
                            key={`${group.key}-${token}`}
                            type="button"
                            className="template-wysiwyg-placeholder-chip"
                            draggable
                            onDragStart={(event) => {
                              event.dataTransfer.setData("text/template-placeholder", token);
                              event.dataTransfer.setData("text/plain", token);
                              event.dataTransfer.effectAllowed = "copy";
                            }}
                            onClick={() => setInsertSignal({ nonce: Date.now(), token })}
                          >
                            {token}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="templates-v2-placeholder-footer">
              <span className="templates-v2-placeholder-footer-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3.8v16.4M3.8 12h16.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="m12 3.8 2.1 2.1M12 3.8 9.9 5.9M12 20.2l2.1-2.1M12 20.2l-2.1-2.1M3.8 12l2.1 2.1M3.8 12l2.1-2.1M20.2 12l-2.1 2.1M20.2 12l-2.1-2.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="9.2" y="9.2" width="5.6" height="5.6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              </span>
              <span className="templates-v2-placeholder-footer-copy">
                <strong>Platzhalter per Klick einfügen</strong>
                <span>oder per Drag &amp; Drop in den Editor ziehen.</span>
              </span>
            </div>
          </div>
        </div>

        <div className="templates-v2-column templates-v2-column--center">
          <div className="templates-v2-editor-shell">
            <div className="templates-v2-pane">
              <div className="templates-v2-editor-header">
                <div>
                  <div className="templates-v2-step-head templates-v2-step-head--editor">
                    <span className="templates-v2-step-icon is-step-3" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M4 20h4l10.5-10.5a1.9 1.9 0 0 0 0-2.7l-1.3-1.3a1.9 1.9 0 0 0-2.7 0L4 16v4Z" stroke="currentColor" strokeWidth="1.7" />
                        <path d="m13 6 5 5" stroke="currentColor" strokeWidth="1.7" />
                      </svg>
                    </span>
                    <div className="templates-v2-editor-head-copy">
                      <h3 className="templates-v2-step-title">Vorlage gestalten</h3>
                      <div className="templates-v2-help-text templates-v2-editor-subtitle">Fügen Sie Platzhalter in den Text ein und gestalten Sie Ihre Vorlage.</div>
                    </div>
                  </div>
                  </div>
                <div className="templates-v2-editor-header-actions">
                  <div className="templates-v2-editor-tabs templates-v2-editor-tabs--header" role="tablist" aria-label="Editor-Bereich">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeEditor === "subject"}
                      className={`templates-v2-editor-tab${activeEditor === "subject" ? " is-active" : ""}`}
                      onClick={() => setActiveEditor("subject")}
                    >
                      Betreff
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeEditor === "body"}
                      className={`templates-v2-editor-tab${activeEditor === "body" ? " is-active" : ""}`}
                      onClick={() => setActiveEditor("body")}
                    >
                      Textvorlage
                    </button>
                  </div>
                  <button type="button" className="templates-v2-open-preview-bottom" onClick={() => openPreviewPanel()} disabled={!selectedTemplate}>
                    <span className="templates-v2-open-preview-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M2.9 12c1.9-3.6 5.4-5.6 9.1-5.6s7.2 2 9.1 5.6c-1.9 3.6-5.4 5.6-9.1 5.6s-7.2-2-9.1-5.6Z" stroke="currentColor" strokeWidth="1.7" />
                        <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.7" />
                      </svg>
                    </span>
                    Vorschau öffnen
                  </button>
                  <button
                    type="button"
                    className="templates-v2-save-button"
                    onClick={() => saveMutation.mutate()}
                    disabled={
                      saveMutation.isPending ||
                      !subject.trim() ||
                      !body.trim() ||
                      !selectedTemplate ||
                      Boolean(subjectLineError) ||
                      Boolean(subjectLengthError) ||
                      Boolean(bodyLengthError)
                    }
                  >
                    <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M4.4 4.4h9.1l2.1 2.1v9.1H4.4z" stroke="currentColor" strokeWidth="1.7" />
                        <path d="M7 4.4v4h6v-4M7.4 15h5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span>{saveMutation.isPending ? "Speichere..." : "Vorlage speichern"}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="templates-v2-pane templates-v2-pane--editor">
            <div className="templates-v2-editor-inner-box">
              <div className="template-shared-toolbar-wrap">
                <div className="template-wysiwyg-toolbar template-wysiwyg-toolbar--shared">
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("undo")} title="Rückgängig">↶</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("redo")} title="Wiederholen">↷</button>
                  <span className="templates-v2-toolbar-divider" />
                  <select className="template-toolbar-select" defaultValue="absatz" aria-label="Format">
                    <option value="absatz">Absatz</option>
                    <option value="ueberschrift">Überschrift</option>
                  </select>
                  <select className="template-toolbar-select" onChange={(e) => fireCommand("fontName", e.target.value)} defaultValue="Arial">
                    <option value="Arial">Arial</option>
                    <option value="Calibri">Calibri</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Courier New">Monospace</option>
                  </select>
                  <select className="template-toolbar-select" onChange={(e) => fireCommand("fontSize", e.target.value)} defaultValue="2">
                    <option value="1">10</option>
                    <option value="2">11</option>
                    <option value="3">12</option>
                    <option value="4">14</option>
                    <option value="5">16</option>
                  </select>
                  <span className="templates-v2-toolbar-divider" />
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("bold")} title="Fett">B</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("italic")} title="Kursiv">I</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("underline")} title="Unterstrichen">U</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("strikeThrough")} title="Durchgestrichen">S</button>
                  <span className="templates-v2-toolbar-divider" />
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("justifyLeft")} title="Linksbündig">⇤</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("justifyCenter")} title="Zentriert">⇆</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("justifyRight")} title="Rechtsbündig">⇥</button>
                  <span className="templates-v2-toolbar-divider" />
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("insertUnorderedList")} title="Aufzählung">•</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("insertOrderedList")} title="Nummerierte Liste">1.</button>
                  <span className="templates-v2-toolbar-divider" />
                  <button
                    type="button"
                    className={`template-toolbar-btn template-toolbar-btn--label${editorViewMode[activeEditor] === "rich" ? " is-active" : ""}`}
                    onClick={() => setEditorViewMode((prev) => ({ ...prev, [activeEditor]: "rich" }))}
                  >
                    Editor
                  </button>
                  <button
                    type="button"
                    className={`template-toolbar-btn template-toolbar-btn--label${editorViewMode[activeEditor] === "html" ? " is-active" : ""}`}
                    onClick={() => setEditorViewMode((prev) => ({ ...prev, [activeEditor]: "html" }))}
                  >
                    HTML
                  </button>
                  <button
                    type="button"
                    className="template-toolbar-btn template-toolbar-btn--icon"
                    onClick={() => {
                      const url = window.prompt("Link-URL eingeben");
                      if (url) fireCommand("createLink", url);
                    }}
                    title="Link einfügen"
                  >
                    🔗
                  </button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--icon" onClick={() => fireCommand("unlink")} title="Link entfernen">⛓</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--label" onClick={() => fireCommand("removeFormat")}>Format löschen</button>
                  <button type="button" className="template-toolbar-btn template-toolbar-btn--label" onClick={() => fireCommand("clear")}>Alles löschen</button>
                </div>
              </div>
              <TemplateWysiwygEditor
                value={activeEditor === "subject" ? subject : body}
                onChange={(next) => {
                  if (activeEditor === "subject") onSubjectChange(next);
                  else onBodyChange(next);
                }}
                placeholders={placeholders}
                placeholder={activeEditor === "subject" ? "Betreff eingeben ..." : "Textvorlage eingeben ..."}
                showToolbar={false}
                showPlaceholderPalette={false}
                onFocus={() => setActiveEditor(activeEditor)}
                isActive
                insertTokenSignal={insertSignal}
                commandSignal={commandSignal}
                showModeToggle={false}
                viewMode={activeEditor === "subject" ? editorViewMode.subject : editorViewMode.body}
                onViewModeChange={(mode) =>
                  setEditorViewMode((prev) => ({ ...prev, [activeEditor]: mode }))
                }
              />
              {activeEditor === "subject" && subjectLineError ? <div className="error-text" style={{ marginTop: 6 }}>{subjectLineError}</div> : null}
              {activeEditor === "subject" && subjectLengthError ? <div className="error-text" style={{ marginTop: 6 }}>{subjectLengthError}</div> : null}
              {activeEditor === "body" && bodyLengthError ? <div className="error-text" style={{ marginTop: 6 }}>{bodyLengthError}</div> : null}
              <div className="templates-v2-editor-meta">
                <span>Wörter: {activeEditor === "subject" ? (plainSubjectText ? plainSubjectText.split(/\s+/).filter(Boolean).length : 0) : wordCount}</span>
                <span>Zeichen: {activeEditor === "subject" ? plainSubjectText.length : characterCount}</span>
                <span className="templates-v2-draft-state">
                  <span className="templates-v2-draft-state-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.8" />
                      <path d="m7.2 10.1 1.9 2 3.8-4.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{formatDraftStatus(selectedTemplate?.updatedAt)}</span>
                </span>
              </div>
            </div>
          </div>
          </div>

          {saveStatus ? <div className="templates-v2-help-text">{saveStatus.text}</div> : null}
        </div>
      </section>
      {previewVisible ? (
        <div className="templates-v2-preview-overlay" role="dialog" aria-modal="true" aria-label="Vorschau">
          <div className="templates-v2-preview-overlay-card">
            <div className="templates-v2-preview-overlay-head">
              <strong>Vorschau</strong>
              <button type="button" onClick={() => setPreviewVisible(false)}>Schließen</button>
            </div>
            <div className="templates-v2-preview-overlay-body">
              {isAnalogTemplate ? (
                pdfPreviewMutation.isPending ? (
                  <div className="templates-v2-inline-preview-state">Lade Vorschau...</div>
                ) : pdfPreviewMutation.isError ? (
                  <div className="error-text">
                    {pdfPreviewMutation.error instanceof Error ? pdfPreviewMutation.error.message : "PDF-Vorschau konnte nicht geladen werden."}
                  </div>
                ) : pdfPreviewUrl ? (
                  <iframe title="PDF-Vorschau" src={`${pdfPreviewUrl}#zoom=100`} className="templates-v2-inline-preview-frame" />
                ) : (
                  <div className="templates-v2-inline-preview-state">Keine PDF-Vorschau verfügbar.</div>
                )
              ) : mailPreviewMutation.isPending ? (
                <div className="templates-v2-inline-preview-state">Lade Vorschau...</div>
              ) : mailPreviewMutation.isError ? (
                <div className="error-text">
                  {mailPreviewMutation.error instanceof Error ? mailPreviewMutation.error.message : "Vorschau konnte nicht geladen werden."}
                </div>
              ) : mailPreviewMutation.data ? (
                <iframe title="Mail-Vorschau" srcDoc={normalizePreviewHtml(mailPreviewMutation.data.html)} className="templates-v2-inline-preview-frame" />
              ) : (
                <div className="templates-v2-inline-preview-state">Keine Vorschau verfügbar.</div>
              )}
            </div>
            {!isAnalogTemplate ? (
              <div className="templates-v2-preview-overlay-testmail">
                <strong>Testmail senden</strong>
                <div className="templates-v2-preview-overlay-testmail-row">
                  <input
                    type="email"
                    value={testRecipientEmail}
                    onChange={(e) => setTestRecipientEmail(e.target.value)}
                    placeholder="empfaenger@beispiel.de"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setTestMailStatus(null);
                      sendTestMailMutation.mutate();
                    }}
                    disabled={sendTestMailMutation.isPending || !selectedTemplate || !mailPreviewMutation.data}
                  >
                    {sendTestMailMutation.isPending ? "Sende..." : "Testmail senden"}
                  </button>
                </div>
                {testMailStatus ? (
                  <div className={`templates-v2-preview-overlay-testmail-status ${testMailStatus.type === "ok" ? "is-ok" : "is-error"}`}>
                    {testMailStatus.text}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
